import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import FormStepper from "@components/form-builder/FormStepper";
import QuestionToolbar from "@components/form-builder/QuestionToolbar";
import TabNavigation from "@components/form-builder/TabNavigation";
import QuestionManager from "@components/form-builder/QuestionManager";
import StepHeader from "@components/form-builder/StepHeader";
import ModalDelete from "@components/common/ModalDelete";
import { useForm } from "@context/FormContext";
import { reindexAllQuestions, renumberTabs } from "@utils/formUtils";

export default function FormQuestions() {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tabToDelete, setTabToDelete] = useState(null);
  const { tabs, setTabs, questionIdCounter, setQuestionIdCounter } = useForm();
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id || "tab1");
  const [showOptions, setShowOptions] = useState(false);

  const toggleOptions = () => {
    setShowOptions((prev) => !prev);
  };

  const handleAddQuestion = (type, icon) => {
    const newId = questionIdCounter;
    setQuestionIdCounter((prev) => prev + 1);
    const newQuestion = {
      id: newId,
      question: "",
      select: type,
      value: "",
      icon,
      required: false,
    };
    if (type === "Multiple Choice" || type === "Checkboxes") {
      newQuestion.options = [{ index: 0, value: "" }];
      newQuestion.hasNotes = false;
    }

    if (type === "Paragraph") {
      newQuestion.isParagraphAnswer = false;
      newQuestion.isListMode = false;
    }

    const updatedTabs = tabs.map((tab) =>
      tab.id === activeTabId
        ? { ...tab, content: [...tab.content, newQuestion] }
        : tab
    );

    const reindexedTabs = reindexAllQuestions(updatedTabs);
    setTabs(reindexedTabs);
    setQuestionIdCounter(reindexedTabs.flatMap((t) => t.content).length + 1);
    setShowOptions(false);
  };

  const handleDeleteTab = (id) => {
    const filtered = tabs.filter((tab) => tab.id !== id);
    const reordered = reindexAllQuestions(renumberTabs(filtered));
    setTabs(reordered);
    setQuestionIdCounter(reordered.flatMap((t) => t.content).length + 1);
    setActiveTabId(reordered[0]?.id || "tab1");
  };

  const handleNext = () => {
    const nonEmptyTabs = tabs.filter((tab) => tab.content.length > 0);
    if (nonEmptyTabs.length === 0) {
      alert("Please add at least one question before proceeding.");
      return;
    }
    const updatedTabs = reindexAllQuestions(renumberTabs(nonEmptyTabs));
    setTabs(updatedTabs);
    setQuestionIdCounter(updatedTabs.flatMap((t) => t.content).length + 1);
    setActiveTabId(updatedTabs[0].id);
    navigate("/form-review");
  };

  const handleAddTab = () => {
    const newIndex = tabs.length + 1;
    const newTab = {
      id: `tab${newIndex}`,
      label: `Page ${newIndex}`,
      content: [],
    };
    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
  };

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-50">
        <Navbar showFieldSearch={false} />
      </div>

      <div className="px-4 mt-4">
        <div className="flex flex-col px-5 py-5 min-h-[calc(100vh-4rem)]">
          <StepHeader title="Create New Form" onClose={() => navigate("/")} />
          <div className="flex-grow">
          <FormStepper currentStep={2} />

          <TabNavigation
            tabs={tabs}
            activeTabId={activeTabId}
            onSwitch={setActiveTabId}
            onDelete={(tab) => {
              if (tab.content.length > 0) {
                setTabToDelete(tab);
                setShowDeleteModal(true);
              } else {
                handleDeleteTab(tab.id);
              }
            }}
            onAdd={handleAddTab}
          />

          <QuestionManager
            activeTabId={activeTabId}
            setQuestionIdCounter={setQuestionIdCounter}
          />

          <QuestionToolbar
            showOptions={showOptions}
            toggleOptions={toggleOptions}
            onAddQuestion={handleAddQuestion}
            onNext={handleNext}
          />
        </div>
      </div>
      </div>
      <ModalDelete
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDeleteTab(tabToDelete.id);
          setShowDeleteModal(false);
        }}
        patientName={tabToDelete?.label}
        contect="This action cannot be undone. Once deleted, the page and all its questions will be permanently deleted."
      />
    </div>
  );
}
